import { create } from "zustand";
import api from "../api/api";

const useOrthophotoStore = create((set, get) => ({
  missions: [],
  selectedMissions: [],
  missionsDownloadedKml: [],
  loadingMissions: false,
  modalOrthophoto: false,

  setLoadingMissions: (value) => set(() => ({ loadingMissions: value })),
  getMissionsKmz: async (projectId) => {
    set({ loadingMissions: true });

    try {
      const { data } = await api.get(`/projects/${projectId}/missions/kmz`);
      set({ missions: data });
    } catch (error) {
      console.error("Error on getMissions:", error);
    } finally {
      set({ loadingMissions: false });
    }
  },

  addSelectedMission: (missionId) =>
    set((state) => ({
      selectedMissions: [...state.selectedMissions, missionId],
    })),

  removeSelectedMission: (missionId) =>
    set((state) => ({
      selectedMissions: state.selectedMissions.filter((id) => id !== missionId),
    })),

  addMissionsDownloadedKml: (downloadedKml) =>
    set((state) => ({
      missionsDownloadedKml: [...state.missionsDownloadedKml, downloadedKml],
    })),

  setModalOrthophoto: (value) => set(() => ({ modalOrthophoto: value })),

  previousLayerIndexes: {},
  updateOrthophoto: (
    map,
    Leaflet,
    zoomLevelInfo,
    layerRefs,
    insideKmlList,
    missionId
  ) => {
    if (!map) return;

    const parser = new DOMParser();

    const useKmlList = insideKmlList[missionId];
    const center = map.getCenter();
    if (!center || !useKmlList) return;

    let auxLayersList = [];
    const idLayer = "layer_" + missionId;
    let layerIndex = 0;
    let diffLoadImages = 0.5;

    if (zoomLevelInfo > 20) {
      layerIndex = 5;
      diffLoadImages = 0.0001;
    } else if (zoomLevelInfo > 19) {
      layerIndex = 4;
      diffLoadImages = 0.0002;
    } else if (zoomLevelInfo > 18) {
      layerIndex = 3;
      diffLoadImages = 0.0004;
    } else if (zoomLevelInfo > 17) {
      layerIndex = 2;
      diffLoadImages = 0.0008;
    } else if (zoomLevelInfo > 16) {
      layerIndex = 1;
      diffLoadImages = 0.01;
    }

    diffLoadImages = Math.max(diffLoadImages * zoomLevelInfo, 0.001);

    const kmzLayers =
      useKmlList[layerIndex] || useKmlList[useKmlList.length - 1];
    if (!kmzLayers) return;

    kmzLayers.forEach((item) => {
      try {
        const parserResult = parser
          .parseFromString(item[1], "text/xml")
          .getElementsByTagName("GroundOverlay")[0]
          .getElementsByTagName("coordinates")[0];

        const coords = parserResult.textContent.split(",0");

        const coordLat = parseFloat(coords[3].split(",")[1]);
        const coordLng = parseFloat(coords[3].split(",")[0]);

        const growFactor = 2;
        const expandedDiff = diffLoadImages * growFactor;

        if (
          coords &&
          coordLat > center.lat - expandedDiff / 2 &&
          coordLat < center.lat + expandedDiff / 2 &&
          coordLng > center.lng - expandedDiff / 2 &&
          coordLng < center.lng + expandedDiff / 2
        ) {
          const layerGroup = new Leaflet.layerGroup();
          const overlay = Leaflet.imageOverlay.rotated(
            item[2],
            Leaflet.latLng(
              parseFloat(coords[3].split(",")[1]),
              parseFloat(coords[3].split(",")[0])
            ),
            Leaflet.latLng(
              parseFloat(coords[2].split(",")[1]),
              parseFloat(coords[2].split(",")[0]) + 0.00000255
            ),
            Leaflet.latLng(
              parseFloat(coords[0].split(",")[1]) - 0.00000255,
              parseFloat(coords[0].split(",")[0])
            ),
            { opacity: 1 }
          );

          overlay.addTo(layerGroup);
          if (!layerGroup.options) layerGroup.options = {};
          layerGroup.options.id = idLayer;
          auxLayersList.push(layerGroup);
        }
      } catch (error) {
        // console.warn("Failed to render KMZ image overlay", error);
      }
    });

    // Validate if the current layer must be updated
    const existingLayers = layerRefs.current[missionId];

    const oldSet = new Set(existingLayers?.map((l) => l._leaflet_id));
    const newSet = new Set(auxLayersList.map((l) => l._leaflet_id));

    const layersToRemove =
      existingLayers?.filter((l) => !newSet.has(l._leaflet_id)) ?? [];
    const layersToAdd = auxLayersList.filter((l) => !oldSet.has(l._leaflet_id));

    const previousLayerIndexes = get().previousLayerIndexes;
    const previousIndex = previousLayerIndexes[missionId];

    // Dont update if the previousIndex are equal to the current and if the previous layers size are equals to the current
    if (
      previousIndex === layerIndex &&
      layersToRemove.length === layersToAdd.length
    )
      return;

    // Update the previous layer index for this missionId
    set((state) => ({
      previousLayerIndexes: {
        ...state.previousLayerIndexes,
        [missionId]: layerIndex,
      },
    }));

    layersToRemove.forEach((layer) => map.removeLayer(layer));
    layersToAdd.forEach((layer) => map.addLayer(layer));

    layerRefs.current[missionId] = auxLayersList;
  },
}));

export default useOrthophotoStore;
